import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';

import Row from 'components/Common/Row';
import { NabTabLinkMargin, NavTabButton } from 'components/NavTab';
import LocalTimelineProvider from 'contextProviders/TimelineProvider/LocalTimelineProvider';
import UsageTab from './UsageTab';
import DetailsCard from './DetailsCard';
import Headings from './Headings';
import Timeline from 'components/Timeline';
import AnalyticsContext from 'contexts/AnalyticsContext';
import SavedFiltersUsageLookupContext from '../context/SavedFiltersUsageLookupContext';
import useSavedFilterTimelineSources from '../hooks/useSavedFilterTimelineSources';
import useSavedFilterTimelineStartsOn from '../hooks/useSavedFilterTimelineStartsOn';

export const TabContent = styled.div`
  height: calc(100vh - 170px);
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
`;

export const TabInner = styled.div`
  overflow-y: auto;
  padding: 1px;
  padding-right: 4px;
`;

const Tabs = ({ filter }: { filter: SavedFilter }) => {
  const { trackEvent } = useContext(AnalyticsContext);
  const { savedFiltersLookup } = useContext(SavedFiltersUsageLookupContext);
  const usageReport = savedFiltersLookup[filter.id];
  const [selectedTab, setSelectedTab] = useState<
    'details' | 'usage' | 'timeline'
  >('details');

  const goToDetails = useCallback(() => {
    setSelectedTab('details');
  }, []);

  const goToUsage = useCallback(() => {
    setSelectedTab('usage');
    trackEvent('Datasets - Filters - Usage opened', {
      filterName: filter.name,
    });
  }, [filter.name, trackEvent]);

  const goToHistory = useCallback(() => {
    setSelectedTab('timeline');
    trackEvent('Datasets - Filters - Timeline opened', {
      filterName: filter.name,
    });
  }, [filter.name, trackEvent]);

  return (
    <div>
      <Row style={{ marginBottom: 16 }}>
        <div style={{ marginRight: NabTabLinkMargin }}>
          <NavTabButton
            onClick={goToDetails}
            isSelected={selectedTab === 'details'}
            title={'Details'}
          />
        </div>
        <div style={{ marginRight: NabTabLinkMargin }}>
          <NavTabButton
            onClick={goToUsage}
            isSelected={selectedTab === 'usage'}
            title={
              usageReport
                ? `Usage (${usageReport.usageCount.toString(10)})`
                : 'Usage'
            }
          />
        </div>
        <NavTabButton
          onClick={goToHistory}
          isSelected={selectedTab === 'timeline'}
          title={'Timeline'}
        />
      </Row>
      <TabContent>
        <TabInner>
          {selectedTab === 'details' && (
            <DetailsCard filter={filter} hasPadding hasBorder />
          )}
          {selectedTab === 'usage' && <UsageTab filter={filter} />}
          {selectedTab === 'timeline' && <Timeline isFullHeight />}
        </TabInner>
      </TabContent>
    </div>
  );
};

const FilterSlideOut = ({
  close,
  filter,
  onEditClicked,
}: {
  close: () => void;
  filter: SavedFilter;
  onEditClicked: () => void;
}) => {
  const sources = useSavedFilterTimelineSources(filter);
  const timelineStartsOn = useSavedFilterTimelineStartsOn(filter);

  return (
    <div>
      <Headings filter={filter} close={close} onEditClicked={onEditClicked} />
      <LocalTimelineProvider sources={sources} startsOn={timelineStartsOn}>
        <Tabs filter={filter} />
      </LocalTimelineProvider>
    </div>
  );
};

export default FilterSlideOut;
